import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { showToast, showLoadingToast } from 'vant'
import 'vant/es/toast/style'

// 1. 定义后端统一返回的接口格式
// 假设后端返回格式为: { code: 200, data: {...}, message: "success" }
export interface ApiResponse<T = any> {
  code: number
  msg: string
  data: T
}

/**
 * 扩展 axios 的请求配置，让业务层可以声明式地控制 loading / 成功提示 / 静默失败
 *
 * 用法示例：
 *   request.post('/api/x', data, { loading: 'Processing...', successMsg: 'Success' })
 *   request.get('/api/y', params, { loading: true })         // 默认文案
 *   request.post('/api/z', data, { silent: true })           // 失败不弹错误 toast，业务自己处理
 */
export interface RequestExtraConfig {
  /** 展示 loading toast，true 使用默认文案 "Loading..."，string 自定义文案 */
  loading?: string | boolean
  /** 成功后自动弹一个普通 toast（在 loading 关闭后延一帧弹出，避免 Vant 单例竞态） */
  successMsg?: string
  /** 失败时不再弹错误 toast（默认会弹）。业务需要自己处理错误提示时用 */
  silent?: boolean
}

export type RequestConfig = AxiosRequestConfig & RequestExtraConfig

// axios 内部使用 InternalAxiosRequestConfig，因此需要合并类型声明，
// 让扩展配置能安全地透传到拦截器
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AxiosRequestConfig extends RequestExtraConfig {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface InternalAxiosRequestConfig extends RequestExtraConfig {}
}

// 2. 创建 axios 实例
const service: AxiosInstance = axios.create({
  // 从环境变量获取 Base URL (需在 .env 文件中配置 VITE_API_URL)
  baseURL: import.meta.env.VITE_API_BASE_URL,
  //   timeout: import.meta.env.VITE_API_TIMEOUT, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
  withCredentials: true,
  // paramsSerializer: (params) => {
  //   const newParams: any = {};
  //   Object.keys(params).forEach((key) => {
  //     if (Array.isArray(params[key])) {
  //       newParams[key] = JSON.stringify(params[key]);
  //     } else {
  //       newParams[key] = params[key];
  //     }
  //   });
  //   return new URLSearchParams(newParams).toString();
  // }
})

/* -------------------------------------------------------------------------- */
/*  全局 Loading 管理器                                                       */
/*                                                                            */
/*  Vant 的 Toast 是全局单例，如果 loading toast 还开着又直接 showToast，会    */
/*  互相冲突（错误 toast 一闪就没、或者 loading 关不掉）。这里用引用计数封装：  */
/*  - 多个并发请求共享同一个 loading 实例                                     */
/*  - 最后一个请求结束时才真正关闭                                            */
/*  - 关闭 loading 之后弹出的 toast 一律用 queueMicrotask 延后一帧            */
/* -------------------------------------------------------------------------- */
let activeLoading: ReturnType<typeof showLoadingToast> | null = null
let loadingCount = 0

const openLoading = (message: string) => {
  loadingCount += 1
  if (loadingCount === 1) {
    activeLoading = showLoadingToast({
      message,
      forbidClick: true,
      duration: 0,
    })
  }
}

const closeLoading = () => {
  if (loadingCount <= 0) return
  loadingCount -= 1
  if (loadingCount === 0 && activeLoading) {
    try {
      activeLoading.close()
    } catch {
      /* ignore */
    }
    activeLoading = null
  }
}

/**
 * 延后再弹 toast，等所有微任务处理完（包括外层 withLoading 的 closeLoading）
 * 才真正显示，避免与 loading 抢 Vant 单例。
 *
 * 为什么不用 queueMicrotask：
 *   axios 拦截器里的 queueMicrotask 入队时机比业务 `await` 后面的 continuation 早，
 *   FIFO 出队时 error toast 会先弹（顶掉 loading），紧接着业务链末尾的 withLoading
 *   closeLoading() 又把 error toast 关掉，造成闪烁。
 *   setTimeout 落到下一个宏任务，所有微任务都跑完，loading 已经真的关了，才轮到弹 toast。
 */
export const showToastNextTick = (message: string) => {
  setTimeout(() => showToast(message), 0)
}

/**
 * 手动包裹多请求 loading：整个 task 期间显示同一个 loading。
 *
 * 场景：一次业务操作需要串多个接口，希望期间只出现一个 loading（不闪烁）。
 * 与请求配置里的 `loading` 共用同一个引用计数，可以互相嵌套。
 *
 * 用法：
 *   await withLoading(async () => {
 *     await api1(payload)
 *     await api2(payload)
 *   }, { message: 'Processing...', successMsg: 'Success' })
 */
export const withLoading = async <T>(
  task: () => Promise<T>,
  options?: { message?: string; successMsg?: string },
): Promise<T> => {
  const msg = options?.message || 'Loading...'
  openLoading(msg)
  try {
    const res = await task()
    closeLoading()
    if (options?.successMsg) showToastNextTick(options.successMsg)
    return res
  } catch (err) {
    closeLoading()
    throw err
  }
}

// 3. 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const getCookie = (name: string) => {
      const m = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
      )
      return m ? decodeURIComponent(m[1] || '') : ''
    }
    const token = getCookie('Authorization')
    if (token) {
      ;(config.headers as any).Authorization = token
    }

    if (config.loading) {
      const msg = typeof config.loading === 'string' ? config.loading : 'Loading...'
      openLoading(msg)
    }

    return config
  },
  (error: any) => {
    return Promise.reject(error)
  },
)

// 4. 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const cfg = response.config as InternalAxiosRequestConfig
    const { code, msg, data } = response.data

    if (code === 200) {
      if (cfg.loading) closeLoading()
      if (cfg.successMsg) showToastNextTick(cfg.successMsg)
      return response.data as any
    } else {
      // 业务失败
      if (cfg.loading) closeLoading()
      const errMsg = msg || '系统错误'
      if (!cfg.silent) {
        // 只要当前有 loading（无论是 config.loading 还是外层 withLoading 打开的），
        // 就延一帧再弹错误 toast，避免与 loading 抢 Vant 单例
        if (loadingCount > 0 || cfg.loading) {
          showToastNextTick(errMsg)
        } else {
          // 没有任何 loading：保持旧行为，同步弹 toast
          showToast(errMsg)
        }
      }
      const err: any = new Error(errMsg)
      err.isToastShown = !cfg.silent
      err.code = code
      err.data = data
      err.raw = response.data
      return Promise.reject(err)
    }
  },
  (error: any) => {
    const cfg = (error.config || {}) as InternalAxiosRequestConfig
    if (cfg.loading) closeLoading()

    let message = ''
    const status = error.response?.status
    const clearAuthorizationCookie = () => {
      try {
        document.cookie = `Authorization=; Max-Age=0; Path=/; SameSite=Lax`
      } catch {
        /* ignore */
      }
    }
    switch (status) {
      case 400:
        message = '请求错误(400)'
        break
      case 401:
        message = '凭证过期，请重新登录'
        clearAuthorizationCookie()
        break
      case 403:
        message = '拒绝访问(403)'
        break
      case 404:
        message = '请求出错(404)'
        break
      case 500:
        message = '服务器错误(500)'
        break
      default:
        message = status ? `连接出错(${status})` : '连接出错'
    }

    if (!cfg.silent) {
      if (loadingCount > 0 || cfg.loading) {
        showToastNextTick(message)
      } else {
        showToast(message)
      }
    }
    error.isToastShown = !cfg.silent
    return Promise.reject(error)
  },
)

// 5. 导出封装后的 request 方法
// 这里对 method 进行封装，便于类型推断
export default {
  get<T = any>(url: string, params?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return service.get(url, { params, ...config })
  },
  post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return service.post(url, data, config)
  },
  put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return service.put(url, data, config)
  },
  delete<T = any>(url: string, params?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return service.delete(url, { params, ...config })
  },
}
